import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Plus,
  Calendar,
  Clock,
  Users,
  QrCode,
  Edit3,
  Trash2,
  Eye,
  Download,
  Search,
  Filter,
  X,
  Copy,
  Check,
  Loader2,
  Book,
  ImagePlus,
} from 'lucide-react'
import { attendanceService, generateServiceQRCode, type ChurchService } from '../../services/attendanceService'
import { BibleVerseLink } from '@/components/BibleVerseLink'
import { supabase } from '@/lib/supabase'
import { AdminUser } from '@/lib/authService'

interface ServicesProps {
  user: AdminUser | null
}

export default function Services({ user }: ServicesProps) {
  console.log('[Services] Component rendering')
  const [services, setServices] = useState<ChurchService[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<string>('All')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedService, setSelectedService] = useState<ChurchService | null>(null)
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('')
  const [copiedCode, setCopiedCode] = useState(false)

  useEffect(() => {
    console.log('[Services] useEffect - loadServices called')
    loadServices()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const loadServices = async () => {
    console.log('[Services] loadServices - starting', { churchId: user?.churchId })
    if (!user?.churchId) {
      console.log('[Services] loadServices - no churchId, returning')
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      console.log('[Services] loadServices - fetching services')
      const data = await attendanceService.getServices(user.churchId, {
        from: 0,
        to: 49
      })
      console.log('[Services] loadServices - services fetched:', data.length)
      setServices(data)
    } catch (error) {
      console.error('[Services] Error loading services:', error)
    } finally {
      console.log('[Services] loadServices - completed')
      setLoading(false)
    }
  }

  const handleCreateService = async (serviceData: Partial<ChurchService>) => {
    if (!user?.churchId) return

    try {
      await attendanceService.createService(user.churchId, serviceData)
      await loadServices()
      setShowCreateModal(false)
    } catch (error) {
      console.error('Error creating service:', error)
      alert('Failed to create service')
    }
  }

  const handleShowQRCode = async (service: ChurchService) => {
    setSelectedService(service)
    try {
      const qrUrl = await generateServiceQRCode(service.check_in_code)
      setQrCodeUrl(qrUrl)
      setShowQRModal(true)
    } catch (error) {
      console.error('Error generating QR code:', error)
      alert('Failed to generate QR code')
    }
  }

  const handleCopyCode = () => {
    if (selectedService) {
      navigator.clipboard.writeText(selectedService.check_in_code)
      setCopiedCode(true)
      setTimeout(() => setCopiedCode(false), 2000)
    }
  }

  const handleDownloadQR = () => {
    if (qrCodeUrl && selectedService) {
      const link = document.createElement('a')
      link.download = `qr-${selectedService.service_name.replace(/\s+/g, '-')}.png`
      link.href = qrCodeUrl
      link.click()
    }
  }

  const handleDeleteService = async (serviceId: string) => {
    if (!confirm('Are you sure you want to delete this service?')) return

    try {
      await attendanceService.deleteService(serviceId)
      await loadServices()
    } catch (error) {
      console.error('Error deleting service:', error)
      alert('Failed to delete service')
    }
  }

  const filteredServices = services.filter(service => {
    const matchesSearch = service.service_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.description?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'All' || service.service_type === filterType

    return matchesSearch && matchesType
  })

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour
    return `${displayHour}:${minutes} ${ampm}`
  }

  const isUpcoming = (dateString: string) => {
    return new Date(dateString) >= new Date()
  }

  const stats = {
    total: services.length,
    upcoming: services.filter(s => isUpcoming(s.service_date)).length,
    active: services.filter(s => s.is_active).length,
    avgAttendance: Math.round(services.reduce((sum, s) => sum + s.actual_attendance, 0) / services.length) || 0
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Church Services</h1>
          <p className="text-muted-foreground">Manage services and generate QR codes for check-in</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Create Service
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Services</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.upcoming}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{stats.active}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Attendance</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.avgAttendance}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search services..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center gap-4">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All Types</SelectItem>
                  <SelectItem value="Sunday Service">Sunday Service</SelectItem>
                  <SelectItem value="Midweek Service">Midweek Service</SelectItem>
                  <SelectItem value="Prayer Meeting">Prayer Meeting</SelectItem>
                  <SelectItem value="Special Event">Special Event</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services List */}
      <div className="grid gap-4 md:grid-cols-2">
        {filteredServices.length === 0 ? (
          <Card className="col-span-2">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No services found. Create your first service!</p>
            </CardContent>
          </Card>
        ) : (
          filteredServices.map((service) => (
            <Card key={service.id} className="hover:border-primary transition-colors overflow-hidden">
              {/* Service Image */}
              {service.service_image_url && (
                <div className="relative h-32 w-full">
                  <img
                    src={service.service_image_url}
                    alt={service.service_name}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg">{service.service_name}</CardTitle>
                    <CardDescription>{service.service_type}</CardDescription>
                  </div>
                  <Badge variant={service.is_active ? 'default' : 'secondary'}>
                    {service.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {service.theme && (
                  <p className="text-sm">
                    <span className="font-medium">Theme:</span> {service.theme}
                  </p>
                )}

                {service.sermon_title && (
                  <div className="space-y-1">
                    <p className="text-sm">
                      <span className="font-medium">Sermon:</span> {service.sermon_title}
                      {service.sermon_speaker && ` by ${service.sermon_speaker}`}
                    </p>
                    {service.sermon_text && (
                      <div className="flex items-center gap-2 text-sm">
                        <Book className="h-3 w-3 text-muted-foreground" />
                        <span className="text-muted-foreground">Text:</span>
                        <BibleVerseLink
                          reference={service.sermon_text}
                          variant="inline"
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    {formatDate(service.service_date)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    {formatTime(service.service_time)}
                    {service.duration_minutes && ` (${service.duration_minutes} min)`}
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    {service.actual_attendance} attended
                    {service.expected_attendance && ` / ${service.expected_attendance} expected`}
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <Button
                    onClick={() => handleShowQRCode(service)}
                    variant="outline"
                    size="sm"
                  >
                    <QrCode className="mr-2 h-4 w-4" />
                    QR Code
                  </Button>

                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm">
                      <Edit3 className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => handleDeleteService(service.id)}
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* QR Code Dialog */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-gray-900">
          <DialogHeader>
            <DialogTitle>QR Code</DialogTitle>
            <DialogDescription>
              {selectedService && `${selectedService.service_name} - ${formatDate(selectedService.service_date)}`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            {qrCodeUrl && (
              <div className="bg-white p-6 rounded-lg">
                <img src={qrCodeUrl} alt="QR Code" className="w-64 h-64" />
              </div>
            )}

            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg w-full justify-center">
              <code className="font-mono text-lg">{selectedService?.check_in_code}</code>
              <Button
                onClick={handleCopyCode}
                variant="ghost"
                size="sm"
              >
                {copiedCode ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button
              onClick={handleDownloadQR}
              variant="outline"
            >
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
            <Button onClick={() => setShowQRModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Service Dialog */}
      {showCreateModal && (
        <CreateServiceDialog
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateService}
        />
      )}
    </div>
  )
}

// Create Service Dialog Component
interface CreateServiceDialogProps {
  onClose: () => void
  onCreate: (data: Partial<ChurchService>) => Promise<void>
}

function CreateServiceDialog({ onClose, onCreate }: CreateServiceDialogProps) {
  const [formData, setFormData] = useState({
    service_name: '',
    service_type: 'Sunday Service',
    service_date: '',
    service_time: '',
    duration_minutes: 90,
    description: '',
    theme: '',
    sermon_title: '',
    sermon_speaker: '',
    sermon_text: '', // Bible verse reference
    expected_attendance: 0,
    allow_self_checkin: true,
    require_location_verification: false,
    service_image_url: ''
  })
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setImageFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return null

    setUploadingImage(true)
    try {
      const fileExt = imageFile.name.split('.').pop()
      const fileName = `service-${Date.now()}.${fileExt}`
      const filePath = `services/${fileName}`

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, imageFile)

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(filePath)

      return publicUrl
    } catch (error) {
      console.error('Error uploading image:', error)
      alert('Failed to upload image')
      return null
    } finally {
      setUploadingImage(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Upload image first if selected
    let imageUrl = formData.service_image_url
    if (imageFile) {
      const uploadedUrl = await uploadImage()
      if (uploadedUrl) {
        imageUrl = uploadedUrl
      }
    }

    const serviceDateTime = new Date(`${formData.service_date}T${formData.service_time}`)
    const windowStart = new Date(serviceDateTime.getTime() - 30 * 60 * 1000)
    const windowEnd = new Date(serviceDateTime.getTime() + 4 * 60 * 60 * 1000)

    await onCreate({
      ...formData,
      service_image_url: imageUrl,
      check_in_window_start: windowStart.toISOString(),
      check_in_window_end: windowEnd.toISOString()
    })
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle>Create New Service</DialogTitle>
          <DialogDescription>Fill in the service details below</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="service_name">Service Name *</Label>
              <Input
                id="service_name"
                required
                value={formData.service_name}
                onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                placeholder="Sunday Morning Worship"
              />
            </div>

            {/* Service Image Upload */}
            <div className="col-span-2">
              <Label>Service Image</Label>
              <p className="text-xs text-muted-foreground mb-2">
                This image will appear on the mobile app's "Today's Service" section
              </p>
              <div className="flex items-start gap-4">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Service preview"
                      className="w-40 h-24 object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute -top-2 -right-2 h-6 w-6 p-0 rounded-full"
                      onClick={() => {
                        setImageFile(null)
                        setImagePreview(null)
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-40 h-24 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors">
                    <ImagePlus className="h-8 w-8 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground mt-1">Upload Image</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageSelect}
                    />
                  </label>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="service_type">Service Type *</Label>
              <Select
                value={formData.service_type}
                onValueChange={(value) => setFormData({ ...formData, service_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sunday Service">Sunday Service</SelectItem>
                  <SelectItem value="Midweek Service">Midweek Service</SelectItem>
                  <SelectItem value="Prayer Meeting">Prayer Meeting</SelectItem>
                  <SelectItem value="Special Event">Special Event</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="service_date">Date *</Label>
              <Input
                id="service_date"
                type="date"
                required
                value={formData.service_date}
                onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="service_time">Time *</Label>
              <Input
                id="service_time"
                type="time"
                required
                value={formData.service_time}
                onChange={(e) => setFormData({ ...formData, service_time: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="duration_minutes">Duration (minutes)</Label>
              <Input
                id="duration_minutes"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="theme">Theme</Label>
              <Input
                id="theme"
                value={formData.theme}
                onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                placeholder="Living by Faith"
              />
            </div>

            <div>
              <Label htmlFor="sermon_title">Sermon Title</Label>
              <Input
                id="sermon_title"
                value={formData.sermon_title}
                onChange={(e) => setFormData({ ...formData, sermon_title: e.target.value })}
                placeholder="The Power of Prayer"
              />
            </div>

            <div>
              <Label htmlFor="sermon_speaker">Speaker</Label>
              <Input
                id="sermon_speaker"
                value={formData.sermon_speaker}
                onChange={(e) => setFormData({ ...formData, sermon_speaker: e.target.value })}
                placeholder="Pastor John Doe"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="sermon_text">Sermon Text (Bible Verse)</Label>
              <div className="flex gap-2">
                <Input
                  id="sermon_text"
                  value={formData.sermon_text}
                  onChange={(e) => setFormData({ ...formData, sermon_text: e.target.value })}
                  placeholder="e.g., John 3:16 or Romans 8:28-39"
                />
                {formData.sermon_text && (
                  <BibleVerseLink
                    reference={formData.sermon_text}
                    variant="button"
                  />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Members will be able to tap on this to open it in their Bible app
              </p>
            </div>

            <div>
              <Label htmlFor="expected_attendance">Expected Attendance</Label>
              <Input
                id="expected_attendance"
                type="number"
                value={formData.expected_attendance}
                onChange={(e) => setFormData({ ...formData, expected_attendance: parseInt(e.target.value) })}
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                placeholder="Service description..."
              />
            </div>

            <div className="col-span-2 space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="allow_self_checkin"
                  checked={formData.allow_self_checkin}
                  onCheckedChange={(checked) => setFormData({ ...formData, allow_self_checkin: checked as boolean })}
                />
                <Label htmlFor="allow_self_checkin" className="font-normal">
                  Allow self check-in from mobile app
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="require_location_verification"
                  checked={formData.require_location_verification}
                  onCheckedChange={(checked) => setFormData({ ...formData, require_location_verification: checked as boolean })}
                />
                <Label htmlFor="require_location_verification" className="font-normal">
                  Require location verification for check-in
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={uploadingImage}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploadingImage}>
              {uploadingImage ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Create Service'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
